import { useEffect, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useSSEAwareQuery } from '../../hooks/useSSEAwareQuery';
import {
  Batch,
  CanvasGraphModel,
  MappingResponse,
  TransformationRuleListResponse,
  apiService,
} from '../../services/api';

export interface UseCanvasQueriesOptions {
  batchId?: string;
  activeWorkbookId?: string;
}

/** Batch statuses that indicate mapping generation is in progress. */
const MAPPING_IN_PROGRESS_STATUSES = new Set(['mapping', 'ready_for_mapping']);

export function useCanvasQueries({ batchId, activeWorkbookId }: UseCanvasQueriesOptions) {
  const queryClient = useQueryClient();

  // Poll batch status so we can detect when mapping generation completes.
  const batchQuery = useSSEAwareQuery<Batch>(
    ['batch', batchId],
    () => apiService.getBatch(batchId!),
    {
      enabled: Boolean(batchId),
      pollingInterval: 10_000,
      ssePollingInterval: false,
      refetchOnWindowFocus: false,
    },
  );
  const batchStatus = batchQuery.data?.status;
  const isMappingInProgress = Boolean(batchStatus && MAPPING_IN_PROGRESS_STATUSES.has(batchStatus));

  // Track the previous mapping-in-progress state so we can detect the
  // transition from "mapping" → "mapped" and refetch the mapping data.
  const wasMappingRef = useRef(isMappingInProgress);
  useEffect(() => {
    if (wasMappingRef.current && !isMappingInProgress && batchId) {
      // Mapping generation just completed — refetch mapping data.
      queryClient.invalidateQueries(['mappings', batchId]);
    }
    wasMappingRef.current = isMappingInProgress;
  }, [isMappingInProgress, batchId, queryClient]);

  // Refetch recipe/mapping when batch moves into post-approval pipeline so metadata (e.g. execution) stays fresh.
  const prevPipelineBatchStatus = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!batchId) return;
    const prev = prevPipelineBatchStatus.current;
    prevPipelineBatchStatus.current = batchStatus;
    const pipeline = ['transforming', 'loading', 'completed', 'failed'];
    if (batchStatus && pipeline.includes(batchStatus) && batchStatus !== prev) {
      queryClient.invalidateQueries(['mappings', batchId]);
    }
  }, [batchStatus, batchId, queryClient]);

  const mappingQuery = useQuery<MappingResponse>(
    ['mappings', batchId],
    () => apiService.getMappings(batchId!),
    {
      enabled: Boolean(batchId),
      refetchOnWindowFocus: false,
      // While mapping is in progress, poll every 10s so we pick up the
      // recipe as soon as generation finishes.
      refetchInterval: isMappingInProgress ? 10_000 : false,
    },
  );
  const mappingResponse = mappingQuery.data;

  const healthQuery = useQuery(
    ['mapping-health', batchId],
    () => apiService.getMappingHealth(batchId!),
    {
      enabled: Boolean(batchId) && Boolean(mappingResponse?.recipe_id),
      refetchOnWindowFocus: false,
    },
  );

  // Issue #656: pending-decisions are fetched eagerly alongside
  // mappings/health so the unified Review pane has all review-state
  // inputs without lazy-loading. The drawer this query used to live
  // in is being deleted.
  const pendingDecisionsQuery = useQuery(
    ['pending-decisions', batchId],
    () => apiService.getPendingDecisions(batchId!),
    {
      enabled: Boolean(batchId) && Boolean(mappingResponse?.recipe_id),
      refetchOnWindowFocus: false,
    },
  );

  const canvasWorkbooksQuery = useQuery<string[]>(
    ['canvas-workbooks'],
    () => apiService.listCanvasWorkbooks(),
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );

  const computedActiveWorkbookId = useMemo(() => {
    if (activeWorkbookId) {
      return activeWorkbookId;
    }
    const recipeId = mappingResponse?.recipe_id;
    if (recipeId) {
      return recipeId;
    }
    const workbooks = canvasWorkbooksQuery.data;
    if (batchId && workbooks?.includes(batchId)) {
      return batchId;
    }
    if (!workbooks || workbooks.length === 0) {
      return undefined;
    }
    return workbooks[0];
  }, [activeWorkbookId, mappingResponse?.recipe_id, batchId, canvasWorkbooksQuery.data]);

  const canvasGraphQuery = useQuery<CanvasGraphModel>(
    ['canvas-graph', activeWorkbookId ?? computedActiveWorkbookId],
    () => apiService.getCanvasGraph((activeWorkbookId ?? computedActiveWorkbookId)!),
    {
      // Wait for mappingQuery to return data before fetching the graph.
      // The mapping-service lazily syncs the canvas workbook as a side
      // effect of GET /mappings/{batchId}, so querying the graph before
      // that completes results in a 404.
      enabled: Boolean(activeWorkbookId ?? computedActiveWorkbookId) && Boolean(mappingResponse),
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  );

  const rulesQuery = useQuery<TransformationRuleListResponse>(
    ['transformation-rules'],
    () => apiService.listTransformationRules(),
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );

  return {
    mappingQuery,
    mappingResponse,
    healthQuery,
    pendingDecisionsQuery,
    canvasWorkbooksQuery,
    canvasGraphQuery,
    rulesQuery,
    computedActiveWorkbookId,
    refetchCanvasGraph: canvasGraphQuery.refetch,
    isMappingInProgress,
    batchStatus,
    batchData: batchQuery.data,
  };
}
